import { useEffect, useState, useCallback } from 'react';
import gettext from '@sdk/lang/gettext';
import useModalState from '@sdk/components/ui/modal/useModalState';
import useAuth from '@sdk/authentication/useAuth';
import useSlingApi from '@sdk/api/useSlingApi';
import getCoordinates from '@sdk/utils/getCoordinates';
import useToast from '@sdk/common/toast/useToast';
import { useField } from '@shopify/react-form';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import getBreaksToAttest from '@sdk/automatedBreaks/helpers/getBreaksToAttest';
import usePromiseModalState from '@sdk/components/ui/modal/usePromiseModalState';
import { ShiftTimesheetsDetailLevel } from '@sdk/shifts/types/apiTypes';
import { AttestationForRequest } from '@sdk/automatedBreaks/types/Attestation';
import { MissedBreaks } from '@sdk/automatedBreaks/types/MissedBreaks';
import { ClockInGeoMode } from '@sdk/authentication/types';
import { shouldApplyAttestationsForShift } from '@sdk/automatedBreaks/utils';
import { BreakCategory } from '@sdk/settings/types';

import { useUserOrgSettings } from '@web/settings/UserOrgSettingsContext';
import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';
import { Props } from '@web/timeClock/ClockOutModal/clockOutModal.types';

const ATTESTATION_SKIPPED_MESSAGE = 'attestations skipped';

type UseShiftWithDetailsProps = {
  shiftId: string;
};

export const useShiftWithDetails = ({ shiftId }: UseShiftWithDetailsProps) => {
  const [shiftWithDetails, setShiftWithDetails] = useState(null);
  const api = useSlingApi();

  useEffect(() => {
    const loadFullShiftDetails = async () => {
      const { data } = await api.shifts.getShift(shiftId, {
        includeTimesheets: ShiftTimesheetsDetailLevel.full,
      });
      setShiftWithDetails(data);
    };

    loadFullShiftDetails();
  }, [api.shifts, shiftId]);

  return {
    shiftWithDetails,
  };
};

export const useClockOutModal = ({ shift, clockOut, onCommentSave }: Props) => {
  const { shiftWithDetails } = useShiftWithDetails({ shiftId: shift.id });
  const { isVisible, open, close } = useModalState();
  const { currentUser } = useAuth();
  const { showErrorToast, showSuccessToast } = useToast();
  const { automatedBreaks, isAttestationsFeatureEnabled } = useAutomatedBreaksSettings();
  const [missedBreaks, setMissedBreaks] = useState<MissedBreaks | null>(null);
  const api = useSlingApi();
  const {
    userOrgSettings: { shiftBreakType },
  } = useUserOrgSettings();

  const attestationsModalState = usePromiseModalState();
  const {
    accept: acceptAttestations,
    decline: declineAttestations,
    isVisible: isAttestationVisible,
    getResult,
  } = attestationsModalState;

  const commentField = useField(shift.assigneeNotes);

  const onCommentChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    commentField.onChange(event.target.value);
  };

  const openAttestations = useCallback(
    async (): Promise<AttestationForRequest[]> => getResult() as Promise<AttestationForRequest[]>,
    [getResult]
  );

  const openClockOutModal = () => {
    open();
  };

  const dismissClockOutModal = () => {
    close();
  };

  const saveComment = async () => {
    try {
      await api.timeClock.editComment(shift.id, commentField.value);
      showSuccessToast(gettext('Your comment has been saved successfully.'));
      onCommentSave({
        shiftId: shift.id,
        comment: commentField.value,
      });
    } catch (e) {
      showErrorToast(getErrorForToast(e));
      throw e;
    }
  };

  const save = async () => {
    if (commentField.value !== commentField.defaultValue) {
      await saveComment();
    }

    const { orgSettings } = currentUser;
    let coords = {};

    if (
      orgSettings.clockInGeoRequired &&
      orgSettings.clockInGeoMode !== ClockInGeoMode.onlyClockIn &&
      orgSettings.clockInMaxDistance
    ) {
      coords = await getCoordinates();
    }

    try {
      const breaksToAttest = getBreaksToAttest(currentUser.orgSettings, shift);
      let attestations = [];
      if (
        shiftBreakType !== BreakCategory.off &&
        isAttestationsFeatureEnabled &&
        shouldApplyAttestationsForShift({
          shift,
          isAttestationsEnabled: isAttestationsFeatureEnabled && automatedBreaks.isAttestation,
        }) &&
        breaksToAttest
      ) {
        setMissedBreaks(breaksToAttest);
        attestations = await openAttestations();
      }
      if (attestations) {
        await clockOut({ coords, attestations });
      } else {
        throw new Error(ATTESTATION_SKIPPED_MESSAGE);
      }
    } catch (e) {
      if (e.message === ATTESTATION_SKIPPED_MESSAGE) {
        throw e;
      } else {
        showErrorToast(getErrorForToast(e));
      }
    }
  };

  return {
    isVisible,
    openClockOutModal,
    dismissClockOutModal,
    missedBreaks,
    acceptAttestations,
    declineAttestations,
    isAttestationVisible,
    save,
    commentField,
    onCommentChange,
    shiftWithDetails,
  };
};
